import React from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  FormControl,
  Grid,
  Input,
  InputLabel,
  styled,
  Theme,
  useMediaQuery,
} from '@mui/material'
import {
  AlertIconType,
  PhoneUtils,
  PuiAlert,
  PuiSelect,
  PuiTextField,
  useFields,
} from '@pbt/pbt-ui-components'

import {
  clearCurrentClient,
  fetchClientByEmail,
  fetchClientByPhone,
  getClientAlreadyExists,
  getClientAlreadyExistsMessage,
  getClientsIsLoading,
  getCurrentClient,
  setClientAlreadyExists,
  setClientType,
  updateCurrentClient,
} from '../../../store/duck/clients'
import { useGetContactMethodWithDisabledFlag } from '../../../store/hooks/clients'
// eslint-disable-next-line import/extensions
import { KioskClient } from '../../../types/entities/clients'
// @ts-ignore
import { useNavigateWithQueryString } from '../../../utils'
import useCallbackWhenLoaded from '../../../utils/useCallbackWhenLoaded'
// @ts-ignore
import PhoneInput from '../../inputs/PhoneInput'
import KioskScreen from '../KioskScreen'

const StyledRoot = styled(Grid)(({ theme }) => ({
  flex: 1,
  paddingLeft: theme.spacing(5),
  paddingRight: theme.spacing(5),
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}))

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  margin: theme.spacing(1, 0),
}))

const StyledInputLabel = styled(InputLabel)(({ theme }) => ({
  color: theme.palette.text.secondary,
}))

const StyledSelect = styled(PuiSelect)(({ theme }) => ({
  '& .MuiSelect-select, & .MuiInputBase-input, & .MuiOutlinedInput-input, & .MuiInput-input':
    {
      whiteSpace: 'pre-line',
      color: `${theme.palette.text.primary} !important`,
    },
}))

const NewClientDetailsScreen = () => {
  const dispatch = useDispatch()
  const navigateWithQueryString = useNavigateWithQueryString()
  const { t } = useTranslation('Common')

  const client = useSelector(getCurrentClient) as KioskClient | null
  const isLoading = useSelector(getClientsIsLoading)
  const clientAlreadyExists = useSelector(getClientAlreadyExists)
  const clientAlreadyExistsMessage = useSelector(getClientAlreadyExistsMessage)
  const ContactMethodWithDisabledFlag = useGetContactMethodWithDisabledFlag()

  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('sm'))
  const spacing = isMobile ? 0 : 4

  const {
    fields: { firstName, lastName, mobilePhone, email, preferredContactMethod },
    validate,
  } = useFields(
    [
      {
        name: 'firstName',
        label: t('Common:FIRST_NAME'),
        validators: ['required'],
        initialValue: client?.firstName || '',
      },
      {
        name: 'lastName',
        label: t('Common:LAST_NAME'),
        validators: ['required'],
        initialValue: client?.lastName || '',
      },
      {
        name: 'mobilePhone',
        label: t('Common:MOBILE_NUMBER'),
        validators: ['phone', 'required'],
        initialValue: PhoneUtils.formatPhoneNumber(client?.mobilePhone || ''),
      },
      {
        name: 'email',
        label: t('Common:EMAIL'),
        validators: ['email', 'required'],
        initialValue: client?.email || '',
      },
      {
        name: 'preferredContactMethod',
        label: t('Common:PREFERRED_CONTACT_METHOD'),
        type: 'select',
        validators: ['required'],
        initialValue: client?.preferredContactMethodId || '',
      },
    ],
    false,
  )

  const openDuplicateDialog = (msg = t('Common:DUPLICATE_CLIENT_MESSAGE')) => {
    dispatch(setClientAlreadyExists(true, msg))
  }

  const proceedAsNewClient = () => {
    const updatedClient = {
      ...client,
      firstName: firstName.value,
      lastName: lastName.value,
      mobilePhone: PhoneUtils.parsePhoneNumber(mobilePhone.value),
      email: email.value,
      preferredContactMethodId: preferredContactMethod.value,
      patients: client?.patients || [],
    }
    dispatch(updateCurrentClient(updatedClient))
    navigateWithQueryString({ url: '/patient-details' })
  }

  const onEmailCheckComplete = () => {
    if (clientAlreadyExists) return
    if (client && client.id) {
      openDuplicateDialog()
      return
    }

    proceedAsNewClient()
  }

  const callbackWhenEmailChecked = useCallbackWhenLoaded(
    onEmailCheckComplete,
    getClientsIsLoading,
  )

  const onPhoneCheckComplete = () => {
    if (clientAlreadyExists) return
    if (client && client.id) {
      openDuplicateDialog()
      return
    }

    // Ensure fetchClientByEmail is dispatched before callback
    dispatch(fetchClientByEmail(email.value))
    callbackWhenEmailChecked()
  }

  const callbackWhenPhoneChecked = useCallbackWhenLoaded(
    onPhoneCheckComplete,
    getClientsIsLoading,
  )

  const onProceed = () => {
    if (!validate()) return
    callbackWhenPhoneChecked()
    dispatch(fetchClientByPhone(PhoneUtils.parsePhoneNumber(mobilePhone.value)))
  }

  const handleCloseAlert = () => {
    dispatch(setClientAlreadyExists(false))
  }

  const onClientFetched = () => {
    dispatch(setClientType('returning'))
    navigateWithQueryString({ url: '/select-patient' })
  }

  const onEmailFetchedForReturn = () => {
    if (client && client.id) {
      onClientFetched()
    }
  }

  const callbackWhenEmailFetchedForReturn = useCallbackWhenLoaded(
    onEmailFetchedForReturn,
    getClientsIsLoading,
  )

  const onPhoneFetchedForReturn = () => {
    if (client && client.id) {
      onClientFetched()
    } else {
      callbackWhenEmailFetchedForReturn()
      dispatch(fetchClientByEmail(email.value))
    }
  }

  const callbackWhenPhoneFetchedForReturn = useCallbackWhenLoaded(
    onPhoneFetchedForReturn,
    getClientsIsLoading,
  )

  const onContinueToReturningFlow = () => {
    handleCloseAlert()
    callbackWhenPhoneFetchedForReturn()
    dispatch(fetchClientByPhone(PhoneUtils.parsePhoneNumber(mobilePhone.value)))
  }

  const onCancelReturningFlow = () => {
    handleCloseAlert()
    dispatch(clearCurrentClient())
    navigateWithQueryString({ url: '/' })
  }

  return (
    <KioskScreen
      justifyContent="flex-start"
      proceedButtonLabel={t('Common:NEXT_ACTION')}
      proceedButtonLoading={isLoading}
      title={t('Common:TELL_US_A_BIT_ABOUT_YOURSELF')}
      onBack={() => navigateWithQueryString({ url: '/client-type-selection' })}
      onProceed={onProceed}
    >
      <StyledRoot container item direction="column" spacing={spacing}>
        <Grid container item alignItems="flex-end" spacing={spacing}>
          <Grid item sm={6} xs={12}>
            <PuiTextField
              fullWidth
              field={firstName}
              inputProps={{ maxLength: 100 }}
              label={`${firstName.label}*`}
              type="text"
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <PuiTextField
              fullWidth
              field={lastName}
              inputProps={{ maxLength: 100 }}
              label={`${lastName.label}*`}
              type="text"
            />
          </Grid>
        </Grid>
        <Grid container item alignItems="flex-end" spacing={spacing}>
          <Grid item sm={6} xs={12}>
            <PhoneInput
              fullWidth
              field={mobilePhone}
              label={`${mobilePhone.label} *`}
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <PuiTextField
              fullWidth
              field={email}
              inputProps={{ maxLength: 100 }}
              label={`${email.label}*`}
              type="email"
            />
          </Grid>
        </Grid>
        <Grid container item alignItems="flex-end" spacing={spacing}>
          <Grid item sm={6} xs={12}>
            <StyledFormControl fullWidth>
              <StyledInputLabel htmlFor="contact-method-select">
                {preferredContactMethod.label}*
              </StyledInputLabel>
              <StyledSelect
                field={preferredContactMethod}
                input={<Input id="contact-method-select" />}
                items={ContactMethodWithDisabledFlag}
              />
            </StyledFormControl>
          </Grid>
        </Grid>
      </StyledRoot>
      <PuiAlert
        cancelButtonText={t('Common:CANCEL_ACTION')}
        iconType={AlertIconType.WARN}
        message={clientAlreadyExistsMessage}
        okButtonText={t('Common:CONTINUE_ACTION')}
        open={clientAlreadyExists}
        onCancel={onCancelReturningFlow}
        onClose={handleCloseAlert}
        onOk={onContinueToReturningFlow}
      />
    </KioskScreen>
  )
}

export default NewClientDetailsScreen
